<script setup lang="ts">
import { ref } from 'vue'
import { useUiStore } from '../stores/uiStore'

interface Props {
  isOpen: boolean
  codes: string[]
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const uiStore = useUiStore()
const copyMessage = ref('')

const handleCopyAll = async () => {
  const codesText = props.codes.join('\n')
  try {
    await navigator.clipboard.writeText(codesText)
    copyMessage.value = '已複製到剪貼簿'
    setTimeout(() => {
      copyMessage.value = ''
    }, 2000)
  } catch (error) {
    copyMessage.value = '複製失敗，請手動複製'
  }
}

const handleClose = () => {
  copyMessage.value = ''
  emit('close')
}
</script>

<template>
  <div v-if="isOpen" class="recovery-overlay" @click="handleClose">
    <div class="recovery-dialog" @click.stop :style="{ fontSize: uiStore.fontSize + 'px' }">
      <div class="recovery-header">
        <h2>恢復碼（請立即儲存）</h2>
        <button class="close-btn" @click="handleClose">×</button>
      </div>

      <div class="recovery-content">
        <div class="warning-box">
          ⚠ 這些碼只顯示一次，關閉後無法再看
        </div>

        <div class="codes-grid">
          <div v-for="(code, index) in codes" :key="index" class="code-item">
            {{ code }}
          </div>
        </div>

        <div v-if="copyMessage" class="copy-message">
          {{ copyMessage }}
        </div>

        <div class="recovery-actions">
          <button class="copy-btn" @click="handleCopyAll">複製全部</button>
          <button class="done-btn" @click="handleClose">我已儲存，關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.recovery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.recovery-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.recovery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.recovery-header h2 {
  margin: 0;
  font-size: 1.25em;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #1f2937;
}

.recovery-content {
  padding: 20px;
}

.warning-box {
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 12px;
  color: #92400e;
  margin-bottom: 20px;
  font-size: 0.95em;
}

.codes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
}

.code-item {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  font-weight: 500;
  text-align: center;
  color: #1f2937;
  user-select: all;
}

.copy-message {
  text-align: center;
  color: #10b981;
  font-size: 0.9em;
  margin-bottom: 12px;
  font-weight: 500;
}

.recovery-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.copy-btn,
.done-btn {
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.copy-btn {
  background: white;
  border: 1px solid #d1d5db;
  color: #1f2937;
}

.copy-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.done-btn {
  background: #3b82f6;
  color: white;
}

.done-btn:hover {
  background: #2563eb;
}
</style>
